import { Injectable, inject } from '@angular/core';
import Swal, { SweetAlertCustomClass, SweetAlertIcon, SweetAlertOptions, SweetAlertResult } from 'sweetalert2';
import { TranslateService } from '../translate';
import { AlertInterface } from './alert.interface';

interface ConfirmOptions {
  title: string;
  message: string;
  confirmButtonText?: string;
  cancelButtonText?: string;
}

interface PromptOptions {
  title: string;
  message: string;
  placeholder?: string;
  confirmButtonText?: string;
  cancelButtonText?: string;
}

@Injectable({ providedIn: 'root' })
export class AlertService implements AlertInterface {
  private readonly translateService = inject(TranslateService);

  private readonly defaultOptions: SweetAlertOptions = {
    confirmButtonColor: '#1B8EB7',
    cancelButtonColor: '#F64846',
  };

  private readonly customClass: SweetAlertCustomClass = {
    confirmButton: 'btn btn-lg btn-primary',
    cancelButton: 'btn btn-lg btn-danger',
  };

  public async message(html: string): Promise<void> {
    const options: SweetAlertOptions = {
      html,
    };
    return Swal.fire({ customClass: this.customClass, ...this.defaultOptions, ...(options as any) }).then(() => {});
  }

  public async success(text: string, title?: string): Promise<void> {
    const icon: SweetAlertIcon = 'success';
    const options: SweetAlertOptions = {
      icon,
      title: title ? title : this.translateService.translate('ALERTA_SUCESSO'),
      html: text,
    };
    return Swal.fire({ customClass: this.customClass, ...this.defaultOptions, ...(options as any) }).then(() => {});
  }

  public async error(text: string, title?: string): Promise<void> {
    const icon: SweetAlertIcon = 'error';
    const options: SweetAlertOptions = {
      icon,
      title: title ? title : this.translateService.translate('ALERTA_ERRO'),
      html: text,
    };
    return Swal.fire({ customClass: this.customClass, ...this.defaultOptions, ...(options as any) }).then(() => {});
  }

  public async warning(text: string, title?: string): Promise<void> {
    const icon: SweetAlertIcon = 'warning';
    const options: SweetAlertOptions = {
      icon,
      title: title ? title : this.translateService.translate('ALERTA_ATENCAO'),
      html: text,
    };
    return Swal.fire({ customClass: this.customClass, ...this.defaultOptions, ...(options as any) }).then(() => {});
  }

  public async info(text: string, title?: string): Promise<void> {
    const icon: SweetAlertIcon = 'info';
    const options: SweetAlertOptions = {
      icon,
      title: title ? title : this.translateService.translate('ALERTA_INFORMACAO'),
      html: text,
    };
    return Swal.fire({ customClass: this.customClass, ...this.defaultOptions, ...(options as any) }).then(() => {});
  }

  public async question(text: string, title?: string): Promise<SweetAlertResult<any>> {
    const icon: SweetAlertIcon = 'question';
    const options: SweetAlertOptions = {
      icon,
      title: title ? title : this.translateService.translate('ALERTA_QUESTAO'),
      html: text,
      showConfirmButton: true,
      confirmButtonText: this.translateService.translate('BOTAO_SIM'),
      showCancelButton: true,
      cancelButtonText: this.translateService.translate('BOTAO_NAO'),
      focusConfirm: false,
      focusCancel: true,
      showCloseButton: true,
    };
    return Swal.fire({ customClass: this.customClass, ...this.defaultOptions, ...(options as any) });
  }

  public async confirm(options: ConfirmOptions): Promise<SweetAlertResult<any>> {
    const icon: SweetAlertIcon = 'question';
    const swalOptions: SweetAlertOptions = {
      icon,
      title: options.title,
      html: options.message,
      showConfirmButton: true,
      confirmButtonText: options.confirmButtonText || this.translateService.translate('BOTAO_SIM'),
      showCancelButton: true,
      cancelButtonText: options.cancelButtonText || this.translateService.translate('BOTAO_NAO'),
      focusConfirm: false,
      focusCancel: true,
      showCloseButton: true,
    };
    return Swal.fire({ customClass: this.customClass, ...this.defaultOptions, ...(swalOptions as any) });
  }

  public async prompt(options: PromptOptions): Promise<SweetAlertResult<any>> {
    const icon: SweetAlertIcon = 'question';
    const swalOptions: SweetAlertOptions = {
      icon,
      title: options.title,
      html: options.message,
      input: 'text',
      inputPlaceholder: options.placeholder ?? '000000',
      inputAttributes: {
        maxlength: '6',
        autocomplete: 'one-time-code',
        inputmode: 'numeric',
      },
      inputValidator: (value: string): string | undefined => {
        const code: string = String(value ?? '').replace(/\D/g, '');
        if (code.length !== 6) {
          return 'Informe o código de 6 dígitos.';
        }
        return undefined;
      },
      showConfirmButton: true,
      confirmButtonText: options.confirmButtonText || this.translateService.translate('BOTAO_SIM'),
      showCancelButton: true,
      cancelButtonText: options.cancelButtonText || this.translateService.translate('BOTAO_NAO'),
      focusConfirm: true,
      showCloseButton: true,
    };
    return Swal.fire({ customClass: this.customClass, ...this.defaultOptions, ...(swalOptions as any) });
  }
}
